import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useReveal } from "../../hooks/useReveal";
import { easing } from "../../lib/animations";
import { MagneticBar } from "./MagneticBar";
import { useCart } from "../../context/CartContext";
import { PRODUCTS, formatPrice } from "../../data/products";

/* ---------- Heading reveal ---------- */
const HeadingReveal = ({ text, inView, baseDelay = 0 }) => {
    const words = text.split(" ");
    let charIdx = 0;
    return (
        <span style={{ display: "inline-block" }}>
            {words.map((word, wi) => (
                <span
                    key={wi}
                    style={{ display: "inline-block", marginRight: "0.18em", whiteSpace: "nowrap" }}
                >
                    {word.split("").map((ch) => {
                        const i = charIdx++;
                        return (
                            <span key={i} style={{ display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}>
                                <motion.span
                                    initial={{ y: "110%" }}
                                    animate={inView ? { y: 0 } : { y: "110%" }}
                                    transition={{ duration: 0.85, ease: easing.primary, delay: baseDelay + i * 0.025 }}
                                    style={{ display: "inline-block" }}
                                >
                                    {ch}
                                </motion.span>
                            </span>
                        );
                    })}
                </span>
            ))}
        </span>
    );
};

/* ---------- Sticky Section label ---------- */
const StickySectionLabel = ({ num }) => (
    <div
        style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: "clamp(120px, 11vw, 200px)",
            pointerEvents: "none",
        }}
    >
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.1, margin: "-25% 0% -25% 0%" }}
            transition={{ duration: 0.7, ease: easing.primary }}
            data-testid={`sticky-section-${num}`}
            style={{
                position: "sticky",
                top: "50vh",
                transform: "translateY(-50%)",
                padding: "0 0 0 clamp(20px, 3vw, 40px)",
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 11,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.7)",
                whiteSpace: "nowrap",
            }}
        >
            <motion.span
                aria-hidden="true"
                animate={{ opacity: [1, 0.2, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    width: 7,
                    height: 7,
                    background: "#c1121f",
                    display: "inline-block",
                    boxShadow: "0 0 4px rgba(193, 18, 31, 0.55)",
                }}
            />
            Section [{num}]
        </motion.div>
    </div>
);

/* ---------- Explore More — action button with magnetic bar ---------- */
const ExploreMore = ({ inView, delay = 0.45 }) => {
    const [hover, setHover] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: 0.9, delay, ease: easing.primary }}
            style={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 8,
                pointerEvents: "auto",
            }}
        >
            <Link
                to="/shop"
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                data-testid="collection-explore-more"
                style={{
                    textDecoration: "none",
                    color: "#fff",
                    display: "inline-flex",
                    flexDirection: "column",
                    gap: 8,
                }}
            >
                <span
                    style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    Explore More
                    <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                        <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#fff" />
                        <path d="M16 1L20 4.5L16 8" stroke="#fff" fill="none" />
                    </svg>
                </span>
                <MagneticBar
                    barCount={60}
                    peakHeight={hover ? 16 : 9}
                    baseHeight={2}
                    falloff={0.08}
                    color="rgba(255,255,255,0.6)"
                    style={{ width: 200, height: 16 }}
                />
            </Link>
        </motion.div>
    );
};

/* =====================================================================
   PRODUCT CARD — gray panel, image centred, hover reveals size picker
   + Add to Cart bar
   ===================================================================== */
const SIZES = ["XS", "S", "M", "L", "XL"];

const ProductCard = ({ item, i, headIn }) => {
    const [hover, setHover] = useState(false);
    const [size, setSize] = useState("M");
    const [added, setAdded] = useState(false);
    const { addItem } = useCart();

    const handleAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem({
            id: item.n,
            slug: item.slug,
            name: item.name,
            priceEUR: item.priceEUR,
            size,
            img: item.cover,
        });
        setAdded(true);
        setTimeout(() => setAdded(false), 1600);
    };

    return (
        <motion.div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            initial={{ opacity: 0, y: 28 }}
            animate={headIn ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
            transition={{
                duration: 0.9,
                delay: 0.1 + (i % 4) * 0.05 + Math.floor(i / 4) * 0.08,
                ease: easing.primary,
            }}
            data-testid={`collection-item-${i}`}
            style={{ position: "relative" }}
        >
            <Link
                to={`/product/${item.slug}`}
                style={{
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        aspectRatio: "1 / 1",
                        overflow: "hidden",
                        background: item.bgTone,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {item.badge && (
                        <span
                            style={{
                                position: "absolute",
                                top: 14,
                                left: 14,
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 9,
                                letterSpacing: "0.24em",
                                textTransform: "uppercase",
                                color: "#0c0c0c",
                                background: "transparent",
                                padding: "4px 8px",
                                zIndex: 2,
                            }}
                        >
                            [ {item.badge} ]
                        </span>
                    )}
                    <span
                        style={{
                            position: "absolute",
                            top: 14,
                            right: 14,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 9,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "rgba(12,12,12,0.55)",
                            zIndex: 2,
                        }}
                    >
                        {item.n}
                    </span>
                    <motion.img
                        layoutId={`product-cover-${item.slug}`}
                        src={item.cover}
                        alt={item.name}
                        draggable={false}
                        style={{
                            width: "82%",
                            height: "82%",
                            objectFit: "contain",
                            transform: hover ? "scale(1.06)" : "scale(1)",
                            transition:
                                "transform 1.4s cubic-bezier(0.22, 1, 0.36, 1)",
                            mixBlendMode: "multiply",
                        }}
                    />

                    <motion.div
                        initial={false}
                        animate={{ y: hover ? "0%" : "100%" }}
                        transition={{ duration: 0.5, ease: easing.primary }}
                        style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 3,
                            background: "rgba(12, 12, 12, 0.93)",
                            backdropFilter: "blur(6px)",
                            padding: "16px 18px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                gap: 6,
                                justifyContent: "space-between",
                            }}
                        >
                            {SIZES.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSize(s);
                                    }}
                                    data-testid={`product-${i}-size-${s}`}
                                    style={{
                                        flex: 1,
                                        background:
                                            size === s ? "#fff" : "transparent",
                                        color: size === s ? "#0c0c0c" : "#fff",
                                        border: "1px solid rgba(255,255,255,0.55)",
                                        fontFamily:
                                            "'Space Grotesk', Manrope, sans-serif",
                                        fontWeight: 600,
                                        fontSize: 11,
                                        letterSpacing: "0.12em",
                                        padding: "6px 0",
                                        cursor: "pointer",
                                        transition: "background 0.25s, color 0.25s",
                                    }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={handleAdd}
                            data-testid={`product-${i}-add`}
                            style={{
                                width: "100%",
                                background: "#fff",
                                color: "#0c0c0c",
                                border: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "12px 0",
                                cursor: "pointer",
                            }}
                        >
                            {added ? "Added · " + size : "Add to Cart"}
                        </button>
                    </motion.div>
                </div>
                <div
                    style={{
                        paddingTop: 14,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        color: "rgba(255,255,255,0.92)",
                    }}
                >
                    <span
                        style={{
                            fontFamily: "'Space Grotesk', Manrope, sans-serif",
                            fontWeight: 500,
                            fontSize: 15,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                        }}
                    >
                        {item.name}
                    </span>
                    <span
                        style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 12,
                            letterSpacing: "0.12em",
                            color: "rgba(255,255,255,0.65)",
                        }}
                    >
                        {formatPrice(item.priceEUR)}
                    </span>
                </div>
            </Link>
        </motion.div>
    );
};

/* =====================================================================
   SECTION 01 — Collection grid (4×2) + Newsletter beneath the grid
   ===================================================================== */
const COLLECTION = PRODUCTS.slice(0, 8);

const CollectionAndNewsletter = () => {
    const [ref, inView] = useReveal({ threshold: 0.06 });
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const onSubmit = (e) => {
        e.preventDefault();
        if (email.length > 3 && email.includes("@")) {
            setSubmitted(true);
            setEmail("");
            setTimeout(() => setSubmitted(false), 4500);
        }
    };

    return (
        <section
            ref={ref}
            id="wedding"
            data-testid="section-collection"
            style={{
                color: "#fff",
                padding: "clamp(60px, 8vw, 130px) clamp(28px, 5vw, 56px) 0",
                marginBottom: "clamp(56px, 7vw, 110px)",
                position: "relative",
            }}
        >
            <StickySectionLabel num="01" />

            {/* CENTERED top block: heading on left? — per spec: heading + sub centred, Explore More left */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(180px, 1fr) 2.2fr minmax(180px, 1fr)",
                    gap: "clamp(24px, 3vw, 56px)",
                    alignItems: "center",
                    marginBottom: "clamp(40px, 5vw, 72px)",
                    paddingBottom: 28,
                }}
            >
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <ExploreMore inView={inView} delay={0.5} />
                </div>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 22,
                    }}
                >
                    <h3
                        style={{
                            margin: 0,
                            fontFamily: "'Space Grotesk', Manrope, sans-serif",
                            fontWeight: 700,
                            fontSize: "clamp(40px, 6vw, 84px)",
                            letterSpacing: "-0.035em",
                            lineHeight: 0.95,
                            textTransform: "uppercase",
                            textAlign: "center",
                        }}
                    >
                        <HeadingReveal text="The Collection" inView={inView} />
                    </h3>
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "'EB Garamond', serif",
                            fontStyle: "italic",
                            fontSize: "clamp(15px, 1.2vw, 19px)",
                            lineHeight: 1.55,
                            color: "rgba(255,255,255,0.78)",
                            maxWidth: "52ch",
                            textAlign: "center",
                        }}
                    >
                        Eight artifacts. Heavy cotton. Honest seams. Each piece is
                        cut, sewn and finished slowly in Poland — built to outlast
                        the season that demanded it.
                    </p>
                </div>

                <div />
            </div>

            {/* 4 × 2 product grid */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "clamp(14px, 1.4vw, 22px)",
                }}
            >
                {COLLECTION.map((item, i) => (
                    <ProductCard key={item.n} item={item} i={i} headIn={inView} />
                ))}
            </div>

            {/* Inline newsletter — centred, no BROADCAST label, no separator line */}
            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                transition={{ duration: 0.9, delay: 0.5, ease: easing.primary }}
                data-testid="inline-newsletter"
                style={{
                    marginTop: "clamp(72px, 8vw, 120px)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 22,
                }}
            >
                <h4
                    style={{
                        margin: 0,
                        fontFamily: "'Space Grotesk', Manrope, sans-serif",
                        fontWeight: 500,
                        fontSize: "clamp(22px, 2vw, 32px)",
                        letterSpacing: "-0.015em",
                        lineHeight: 1.18,
                        color: "#fff",
                        textAlign: "center",
                    }}
                >
                    Get early access for new releases.
                </h4>

                {!submitted ? (
                    <form
                        onSubmit={onSubmit}
                        data-testid="early-access-form"
                        style={{
                            display: "flex",
                            alignItems: "stretch",
                            borderBottom: "1px solid rgba(255,255,255,0.35)",
                            paddingBottom: 4,
                            gap: 18,
                            width: "100%",
                            maxWidth: 540,
                        }}
                    >
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            data-testid="early-access-email"
                            style={{
                                flex: 1,
                                background: "transparent",
                                border: "none",
                                outline: "none",
                                color: "#fff",
                                fontFamily: "'Space Grotesk', Manrope, sans-serif",
                                fontSize: 16,
                                padding: "12px 0",
                                letterSpacing: "0.01em",
                                textAlign: "center",
                            }}
                        />
                        <button
                            type="submit"
                            data-testid="early-access-submit"
                            style={{
                                background: "transparent",
                                border: "none",
                                color: "#fff",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                cursor: "pointer",
                                padding: "12px 0",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            Subscribe
                            <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                                <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#fff" />
                                <path d="M16 1L20 4.5L16 8" stroke="#fff" fill="none" />
                            </svg>
                        </button>
                    </form>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: easing.primary }}
                        data-testid="newsletter-success"
                        style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.75)",
                            paddingTop: 12,
                            paddingBottom: 6,
                            borderBottom: "1px solid rgba(255,255,255,0.35)",
                            width: "100%",
                            maxWidth: 540,
                        }}
                    >
                        Signal received. Stay quiet.
                    </motion.div>
                )}
            </motion.div>
        </section>
    );
};

/* =====================================================================
   SECTION 02 — Manifest with Read More expand
   ===================================================================== */
const MANIFEST_INTRO =
    "The world keeps talking. Most of it means nothing. Your instinct knew first. We just design for the second voice.";

const MANIFEST_FULL = [
    "We do not sell hype. Hype is a tax on uncertainty, and we are not interested in it.",
    "We make objects. Heavy cotton. Honest seams. Made slowly in Poland by people who answer their own phone.",
    "Quality survives trends. Silence is luxury. The product speaks first. The marketing, if it appears at all, arrives much later, in lowercase.",
    "If a piece does not earn its place in your wardrobe within thirty days, we want it back. If it does, we ask only that you wear it long enough to forget when you bought it.",
    "This is the only manifesto we plan to write. The rest will be visible in the work.",
];

const ManifestSection = () => {
    const [ref, inView] = useReveal({ threshold: 0.12 });
    const [expanded, setExpanded] = useState(false);
    return (
        <section
            ref={ref}
            id="creation"
            data-testid="section-manifest"
            style={{
                color: "#fff",
                padding: "clamp(40px, 6vw, 80px) clamp(28px, 5vw, 56px) 0",
                position: "relative",
            }}
        >
            <StickySectionLabel num="02" />

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 26,
                }}
            >
                <h3
                    style={{
                        margin: 0,
                        fontFamily: "'Space Grotesk', Manrope, sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(42px, 6vw, 84px)",
                        letterSpacing: "-0.035em",
                        lineHeight: 0.95,
                        textTransform: "uppercase",
                        textAlign: "center",
                    }}
                >
                    <HeadingReveal text="Manifest" inView={inView} />
                </h3>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 22,
                        width: "100%",
                    }}
                >
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: easing.primary }}
                        style={{
                            margin: 0,
                            fontFamily: "'EB Garamond', serif",
                            fontStyle: "italic",
                            fontSize: "clamp(20px, 1.8vw, 30px)",
                            lineHeight: 1.42,
                            color: "rgba(255,255,255,0.92)",
                            maxWidth: "44ch",
                            textAlign: "center",
                        }}
                    >
                        {MANIFEST_INTRO}
                    </motion.p>

                    <motion.div
                        initial={false}
                        animate={{
                            height: expanded ? "auto" : 0,
                            opacity: expanded ? 1 : 0,
                        }}
                        transition={{ duration: 0.7, ease: easing.primary }}
                        data-testid="manifest-fulltext"
                        style={{
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 22,
                            width: "100%",
                        }}
                    >
                        <div style={{ height: 8 }} />
                        {MANIFEST_FULL.map((para, i) => (
                            <p
                                key={i}
                                style={{
                                    margin: 0,
                                    fontFamily: "'Space Grotesk', Manrope, sans-serif",
                                    fontSize: "clamp(15px, 1.15vw, 18px)",
                                    lineHeight: 1.65,
                                    color: "rgba(255,255,255,0.78)",
                                    maxWidth: "60ch",
                                    letterSpacing: "-0.005em",
                                    textAlign: "center",
                                }}
                            >
                                {para}
                            </p>
                        ))}
                    </motion.div>

                    <button
                        onClick={() => setExpanded((s) => !s)}
                        data-testid="manifest-read-more"
                        aria-expanded={expanded}
                        style={{
                            marginTop: 12,
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.45)",
                            color: "#fff",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            padding: "14px 28px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 14,
                            transition: "border-color 0.4s, background 0.4s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "rgba(255,255,255,1)";
                            e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)";
                            e.currentTarget.style.background = "transparent";
                        }}
                    >
                        {expanded ? "Collapse" : "Read More"}
                        <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                            <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#fff" />
                            <path
                                d="M16 1L20 4.5L16 8"
                                stroke="#fff"
                                fill="none"
                                style={{
                                    transform: expanded ? "rotate(90deg)" : "none",
                                    transformOrigin: "18px 4.5px",
                                    transition: "transform 0.5s",
                                }}
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </section>
    );
};

/* =====================================================================
   Wrapper — no top heading, sections stacked
   ===================================================================== */
const OurTwoSections = () => {
    return (
        <section
            className="sections-block"
            data-testid="our-two-sections"
            style={{
                background: "#0c0c0c",
                color: "#fff",
                padding: "0 0 clamp(40px, 6vw, 100px)",
                position: "relative",
            }}
        >
            <CollectionAndNewsletter />
            <ManifestSection />
        </section>
    );
};

export default OurTwoSections;
